import { useCallback, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import AiPlanActiveSwitch from "../AiPlanActiveSwitch";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
  type GridPinnedColumnFields,
  type GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import noDataAnim from "../../../assets/no-data-anim.webp";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { AUTO_TAGGING_PRIVILEGES } from "../../../services/permissionsService";
import { TruncatedTooltipCell } from "../TruncatedTooltipCell";
import DeleteTaggingPlanConfirmDialog from "../TaggingPlan/DeleteTaggingPlanConfirmDialog";
import TaggingPlanActiveConfirmDialog, {
  type TaggingPlanActiveConfirmMode,
} from "../TaggingPlan/TaggingPlanActiveConfirmDialog";
import TaggingPlanRowActions from "../TaggingPlan/TaggingPlanRowActions";
import TriagePlanTableToolbar from "./TriagePlanTableToolbar";
import type { TriagePlanRow } from "./types";

const TRIAGE_PLAN_DELETE_DESCRIPTION =
  "Are you sure you want to delete this triage rule? This action cannot be undone. Flows that have already been triggered will not be rolled back.";

type TriagePlanTableProps = Readonly<{
  rows: TriagePlanRow[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  onAddNew?: () => void;
  onEdit?: (row: TriagePlanRow) => void;
  onActiveChange?: (rowId: string, enabled: boolean) => void;
  onDeleteRow?: (rowId: string) => void;
}>;

function TriagePlanNoRowsOverlay({ onAddNew }: { onAddNew?: () => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        minHeight: 280,
        py: 4,
        boxSizing: "border-box",
        bgcolor: "background.paper",
      }}
    >
      <Box
        component="img"
        src={noDataAnim}
        alt=""
        sx={{ width: 160, height: "auto", mb: 2 }}
      />
      <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>
        No data available.{" "}
        {onAddNew ? (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={onAddNew}
            sx={{
              color: "#295EF1",
              fontSize: "14px",
              fontWeight: 500,
              verticalAlign: "baseline",
              cursor: "pointer",
            }}
          >
            Add Now
          </Link>
        ) : null}
      </Typography>
    </Box>
  );
}

function renderTextCell(value: unknown) {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          width: "100%",
        }}
      >
        <Typography sx={{ color: "#8c8c8c", fontSize: "14px" }}>-</Typography>
      </Box>
    );
  }
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        height: "100%",
        width: "100%",
        minWidth: 0,
      }}
    >
      <TruncatedTooltipCell text={text} />
    </Box>
  );
}

export default function TriagePlanTable({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  onAddNew,
  onEdit,
  onActiveChange,
  onDeleteRow,
}: TriagePlanTableProps) {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const { showMessage } = useSnackbar();
  const canManageAutoTagging = useHasPrivilege(AUTO_TAGGING_PRIVILEGES.manage);
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const manageColumnsPanelScrollTopRef = useRef(0);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  });
  const [deleteTarget, setDeleteTarget] = useState<TriagePlanRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [activeToggleTarget, setActiveToggleTarget] = useState<{
    row: TriagePlanRow;
    nextActive: boolean;
  } | null>(null);
  const [updatingActive, setUpdatingActive] = useState(false);

  const activeConfirmMode: TaggingPlanActiveConfirmMode | null =
    activeToggleTarget
      ? activeToggleTarget.nextActive
        ? "activate"
        : "deactivate"
      : null;

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={manageColumnsPanelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const noRowsOverlaySlot = useCallback(
    () => (
      <TriagePlanNoRowsOverlay
        onAddNew={canManageAutoTagging ? onAddNew : undefined}
      />
    ),
    [canManageAutoTagging, onAddNew],
  );

  const handleActiveToggleRequest = useCallback(
    (row: TriagePlanRow, nextActive: boolean) => {
      if (!canManageAutoTagging) return;
      setActiveToggleTarget({ row, nextActive });
    },
    [canManageAutoTagging],
  );

  const handleCloseActiveDialog = useCallback(() => {
    if (updatingActive) return;
    setActiveToggleTarget(null);
  }, [updatingActive]);

  const handleConfirmActiveToggle = useCallback(async () => {
    if (!activeToggleTarget || updatingActive) return;
    setUpdatingActive(true);
    try {
      onActiveChange?.(
        activeToggleTarget.row.id,
        activeToggleTarget.nextActive,
      );
      showMessage(
        activeToggleTarget.nextActive
          ? "Triage rule activated."
          : "Triage rule deactivated.",
        "success",
      );
      setActiveToggleTarget(null);
    } finally {
      setUpdatingActive(false);
    }
  }, [activeToggleTarget, onActiveChange, showMessage, updatingActive]);

  const handleEditRequest = useCallback(
    (row: TriagePlanRow) => {
      if (onEdit) {
        onEdit(row);
        return;
      }
      showMessage("Triage rule editing is coming soon.", "info");
    },
    [onEdit, showMessage],
  );

  const handleDeleteRequest = useCallback((row: TriagePlanRow) => {
    setDeleteTarget(row);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    if (deleting) return;
    setDeleteTarget(null);
  }, [deleting]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      onDeleteRow?.(deleteTarget.id);
      showMessage("Triage rule deleted.", "success");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, deleting, onDeleteRow, showMessage]);

  const columns = useMemo<GridColDef<TriagePlanRow>[]>(
    () => [
      {
        field: "conditionDescription",
        headerName: "Condition",
        flex: 1.6,
        minWidth: 320,
        sortable: false,
        filterable: false,
        renderCell: ({ value }) => renderTextCell(value),
      },
      {
        field: "processFlowName",
        headerName: "Process Flow",
        flex: 1,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: ({ value }) => renderTextCell(value),
      },
      {
        field: "enabled",
        headerName: "Active",
        width: 88,
        sortable: false,
        filterable: false,
        resizable: false,
        disableExport: true,
        align: "center",
        headerAlign: "center",
        cellClassName: "ai-plan-status-cell",
        renderCell: ({ row }) => (
          <AiPlanActiveSwitch
            checked={row.enabled}
            disabled={!canManageAutoTagging}
            onChange={(nextChecked) =>
              handleActiveToggleRequest(row, nextChecked)
            }
          />
        ),
      },
      ...(canManageAutoTagging
        ? [
            {
              field: "actions",
              headerName: "Actions",
              width: 88,
              sortable: false,
              filterable: false,
              resizable: false,
              disableExport: true,
              align: "center" as const,
              headerAlign: "center" as const,
              cellClassName: "ai-plan-actions-cell",
              renderCell: ({ row }: { row: TriagePlanRow }) => (
                <TaggingPlanRowActions
                  onEdit={() => handleEditRequest(row)}
                  onDelete={() => handleDeleteRequest(row)}
                />
              ),
            } satisfies GridColDef<TriagePlanRow>,
          ]
        : []),
    ],
    [
      canManageAutoTagging,
      handleActiveToggleRequest,
      handleDeleteRequest,
      handleEditRequest,
    ],
  );

  const isEmpty = rows.length === 0;

  // Match Tagging Plan: Active then Actions (left → right). Controlled prop
  // so order is not stuck on a stale initialState after HMR.
  const pinnedColumns = useMemo<GridPinnedColumnFields>(
    () => ({
      right: canManageAutoTagging ? ["enabled", "actions"] : ["enabled"],
    }),
    [canManageAutoTagging],
  );

  return (
    <>
      <Box
        className={`${styles.dataGridPanel}${
          isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
        }`}
        sx={{
          ...aiPlanDataGridPanelSx,
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 480,
        }}
      >
        <DataGridPremium
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          rowHeight={48}
          columnHeaderHeight={48}
          checkboxSelection
          disableRowSelectionOnClick
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={setSelectionModel}
          disableRowGrouping
          disableAggregation
          disablePivoting
          disableColumnSorting
          pagination
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          pageSizeOptions={[5, 10, 20, 50]}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          showToolbar
          showColumnVerticalBorder={false}
          disableColumnPinning={false}
          pinnedColumnsSectionSeparator="border"
          pinnedColumns={pinnedColumns}
          slots={{
            toolbar: TriagePlanTableToolbar,
            noRowsOverlay: noRowsOverlaySlot,
            columnsManagement: columnsManagementSlot,
          }}
          slotProps={{
            toolbar: { onAddNew } as never,
          }}
          sx={
            [
              getRegulationsListDataGridSx(primaryColor),
              ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
              {
                height: "100%",
                minHeight: 400,
                "& .ai-plan-status-cell, & .ai-plan-actions-cell": {
                  display: "flex !important",
                  alignItems: "center !important",
                  justifyContent: "center !important",
                  "& .MuiDataGrid-cellContent": {
                    display: "flex !important",
                    alignItems: "center !important",
                    justifyContent: "center !important",
                    width: "100%",
                    height: "100%",
                    overflow: "visible",
                  },
                },
                "& .ai-plan-actions-cell.MuiDataGrid-cell--pinnedRight": {
                  boxShadow: "none",
                },
                "& .ai-plan-status-cell.MuiDataGrid-cell--pinnedRight": {
                  boxShadow: "none",
                },
              },
            ] as SxProps<Theme>
          }
        />
      </Box>

      <DeleteTaggingPlanConfirmDialog
        open={deleteTarget !== null}
        deleting={deleting}
        description={TRIAGE_PLAN_DELETE_DESCRIPTION}
        onClose={handleCloseDeleteDialog}
        onConfirm={() => void handleConfirmDelete()}
      />
      <TaggingPlanActiveConfirmDialog
        open={activeToggleTarget !== null}
        mode={activeConfirmMode}
        saving={updatingActive}
        onClose={handleCloseActiveDialog}
        onConfirm={() => void handleConfirmActiveToggle()}
      />
    </>
  );
}
